import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { endpoints } from '../../../helper/endpoints';
import {
	AbnClient,
	AcnClient,
	ClientEntityGroup,
	GetAbnClientsByNameResponse,
	GetClientSuboccupationResponse,
	Suboccupation,
} from '../../../shared/types/quotes';
import { axiosBaseQuery } from '../axiosBaseQuery';
import { quoteApi } from '../quoteApi/quoteApi';

interface SanctionsClientRequest {
	businessNumber: string;
	name: string;
	type: string;
}

interface UpdateClientRequestClient {
	id: number;
	// businessNumber?: string;
	// organizationName?: string;
	// description?: string;
	// website?: string;
	// phone?: string;
	// email?: string;
	address1: string;
	address2: string;
	postcode: string;
	stateCode: string;
	// countryId?: number;
	// countryName?: string;
	// suboccupationId?: number;
	// suboccupationName?: string;
	// clientEntityId?: number;
	// clientEntityName?: string;
	// clientAbnEntityCode?: string;
}

interface UpdateClientRequest {
	quoteId: number;
	clientData: UpdateClientRequestClient;
}

export interface ClientSearchResult {
	businessNumber: string;
	isCurrentName: string;
	organizationName: string;
	isCurrentAddress: string;
	postcode: string;
	stateCode: string;
	searchRelevance: string;
	fullName: string | null;
	givenName: string | null;
	otherGivenName: string | null;
	familyName: string | null;
	tradingName: string | null;
	businessName: string | null;
	clientHash: string;
}

interface ClientSearchResponse {
	result: ClientSearchResult[];
}

interface AcnClientSearchResponse {
	result: AcnClient;
}

interface AcnClientSearchRequest {
	acn: string;
	acnEntityTypeCode: string;
}

interface ClientAcnSearchByNameRequest {
	clientName: string;
	acnEntityTypeCode: string;
}

// Define a service using a base URL and expected endpoints
export const clientApi = createApi({
	reducerPath: 'clientApi',
	baseQuery: axiosBaseQuery({ baseUrl: '' }),
	endpoints: (builder) => ({
		getClientByABN: builder.query<any, string>({
			query: (abn) => ({
				url: endpoints.GET_ABN_CLIENT_BY_ABN_NUMBER,
				method: 'POST',
				data: JSON.stringify(abn),
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
			}),
			transformErrorResponse: (response) => {
				console.log('ERROR RESPONSE getClientByABN', response);
				return response;
			},
			transformResponse: (response) => {
				// console.log('getClientByABN', response);
				return response;
			},
		}),
		getClientByACN: builder.query<AcnClientSearchResponse, AcnClientSearchRequest>({
			query: ({ acn, acnEntityTypeCode }) => ({
				url: endpoints.GET_ACN_CLIENT_BY_ACN_NUMBER,
				method: 'POST',
				data: { acn, acnEntityTypeCode },
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
			}),
			transformErrorResponse: (response) => {
				console.log('ERROR RESPONSE getClientByACN', response);
				return response;
			},
			transformResponse: (response) => {
				// console.log('getClientByACN', response);
				return response;
			},
		}),
		getClientsByName: builder.query<ClientSearchResponse, string>({
			query: (name) => ({
				url: endpoints.GET_ABN_CLIENTS_BY_NAME,
				method: 'POST',
				data: JSON.stringify(name),
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
			}),
			transformErrorResponse: (response) => {
				console.log('ERROR RESPONSE getClientsByName', response);
				return response;
			},
			transformResponse: (response) => {
				// console.log('getClientsByName', response);
				let newArray = response.result.slice() as ClientSearchResult[];
				const nameValue = (obj: ClientSearchResult) =>
					obj.organizationName ??
					obj.fullName ??
					(obj.givenName != null && obj.familyName != null
						? `${obj.givenName} ${obj.familyName}`
						: '');
				newArray.sort((a: ClientSearchResult, b: ClientSearchResult) =>
					nameValue(a).localeCompare(nameValue(b))
				);
				return { result: newArray };
			},
		}),
		getACNClientsByName: builder.query<AcnClient[], ClientAcnSearchByNameRequest>({
			query: ({ clientName, acnEntityTypeCode }) => ({
				url: endpoints.GET_ACN_CLIENTS_BY_NAME,
				method: 'POST',
				data: { clientName, acnEntityTypeCode },
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
			}),
			transformErrorResponse: (response) => {
				console.log('ERROR RESPONSE getACNClientsByName', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('getACNClientsByName', response);
				let newArray = response.result.slice() as AcnClient[];
				const nameValue = (obj: AcnClient) => obj.name.nameValue;
				newArray.sort((a: AcnClient, b: AcnClient) =>
					nameValue(a).localeCompare(nameValue(b))
				);
				return newArray;
			},
		}),
		getClientOccupationByABN: builder.query<any, string>({
			keepUnusedDataFor: 30,
			query: (abn) => ({
				url: endpoints.GET_CLIENT_SUBOCCUPATION_BY_ABN_NUMBER,
				method: 'POST',
				data: JSON.stringify(abn),
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
			}),
			transformResponse: (response: GetClientSuboccupationResponse) => {
				const newOccupations = response
					.flatMap((occupation) => occupation.subOccupations)
					.sort((a: Suboccupation, b: Suboccupation) =>
						a.name.toLowerCase().localeCompare(b.name.toLowerCase())
					);
				// console.log('RESPONSE getClientOccupationByABN', response, newOccupations);
				return newOccupations;
			},
		}),
		getClientEntityGroup: builder.query<ClientEntityGroup[], undefined>({
			query: () => ({
				url: endpoints.GET_CLIENT_ENTITY_GROUP,
				method: 'POST',
				data: null,
				// data: JSON.stringify(abn),
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
			}),
			transformResponse: (response: ClientEntityGroup[]) => {
				// console.log('RESPONSE getClientEntityGroup', response);
				return response;
			},
		}),
		checkClientSanctions: builder.mutation<any, SanctionsClientRequest>({
			query: ({ businessNumber, name, type }) => ({
				url: endpoints.SEARCH_SANCTIONS_BY_CLIENT,
				method: 'POST',
				data: { businessNumber, organizationName: name, type },
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
			}),
			transformResponse: (response) => {
				// console.log('RESPONSE checkClientSanctions', response);
				// localStorage.setItem("quoteId", response.id.toString())
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					dispatch(quoteApi.util.resetApiState());
				} catch (error: any) {
					console.log('Error checking client sanctions', error);
				}
			},
		}),
		updateClient: builder.mutation<any, UpdateClientRequest>({
			query: ({ quoteId, clientData }) => ({
				url: endpoints.SAVE_CLIENT_TO_DB ?? '',
				method: 'POST',
				data: clientData,
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
			}),
			transformResponse: (response) => {
				// console.log('RESPONSE updateClient', response);
				return response;
			},
			onQueryStarted: async ({ quoteId }, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					dispatch(
						quoteApi.util.invalidateTags([
							{ type: 'QUOTE', id: quoteId },
							{ type: 'QUOTES', id: 'LIST' },
						])
					);
					// dispatch(clearError());
				} catch (error: any) {
					// dispatch(createError(error));
					console.log('Error updating client', error);
				}
			},
		}),
	}),
});

// Export hooks for usage in functional components
export const {
	useGetClientByABNQuery,
	useGetClientByACNQuery,
	useGetClientsByNameQuery,
	useGetClientOccupationByABNQuery,
	useGetACNClientsByNameQuery,
	useLazyGetACNClientsByNameQuery,
	useLazyGetClientByABNQuery,
	useLazyGetClientByACNQuery,
	useLazyGetClientsByNameQuery,
	useGetClientEntityGroupQuery,
	useCheckClientSanctionsMutation,
	useUpdateClientMutation,
} = clientApi;
